import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ScanRequestMeter, TargetId } from '@sona/shared-model-common';
import { LatestReportMetrics, HistoricalReportMetrics, Report } from '@sona/shared-reports-models';
import {
    IScanRequest,
    ScanCategory,
    ScanRequestId
} from '@sona/shared-scan-model-common';
import { Paginated } from 'nestjs-paginate';
import { SseClient } from 'ngx-sse-client';
import { firstValueFrom, map, Observable } from 'rxjs';

import { Page } from '../models/page';
import { PaginationParams } from '../models/pagination-params';
import { createPaginationSearchParams } from '../utils/api-utils';
import {
    ScanRequestChanges,
    ScanRequestRescanChanges
} from '../models/scan-request-changes';

@Injectable({
    providedIn: 'root'
})
export class LatestDeepScanApi {
    private readonly _baseUrl = '/api/reports/deep/targets';
    private readonly _sseClient = inject(SseClient);
    private readonly _http = inject(HttpClient);


    createDeepScan(targetId: TargetId, meter: ScanRequestMeter): Promise<IScanRequest>;
    createDeepScan(targetId: null | undefined, meter: ScanRequestMeter): Promise<never>;
    createDeepScan(targetId: TargetId | null | undefined, meter: ScanRequestMeter): Promise<IScanRequest | never>;
    async createDeepScan(targetId: TargetId | null | undefined, meter: ScanRequestMeter) {
        if (!targetId) {
            throw new Error('`targetId` is required');
        }

        return firstValueFrom(this._http.post<IScanRequest>(`${this._baseUrl}/${targetId}`, { meter }));
    }

    getLatestReportMetrics(targetId: TargetId): Observable<LatestReportMetrics>;
    getLatestReportMetrics(targetId: null | undefined): never;
    getLatestReportMetrics(targetId: TargetId | null | undefined): Observable<LatestReportMetrics> | never;
    getLatestReportMetrics(targetId: TargetId | null | undefined) {
        if (!targetId) {
            throw new Error('`targetId` is required');
        }

        return this._http.get<LatestReportMetrics>(`${this._baseUrl}/${targetId}/latest-report/metrics`);
    }

    checkMetricsExist(targetId: TargetId | null): Observable<{ metricsExist: boolean }> {
        if (!targetId) {
            throw new Error('`targetId` is required');
        }

        return this._http.get<{ metricsExist: boolean }>(`${this._baseUrl}/${targetId}/latest-report/metrics/exists`);
    }

    getLatestReportChanges(targetId: TargetId): Observable<ScanRequestChanges>;
    getLatestReportChanges(targetId: null | undefined): never;
    getLatestReportChanges(targetId: TargetId | null | undefined): Observable<ScanRequestChanges> | never;
    getLatestReportChanges(targetId: TargetId | null | undefined) {
        if (!targetId) {
            throw new Error('`targetId` is required');
        }

        return this._sseClient.stream(`${this._baseUrl}/${targetId}/latest-report/changes`, {
            keepAlive: true,
            reconnectionDelay: 1000
        }, {}, 'GET').pipe(
            map((event) => JSON.parse((event as MessageEvent<string>).data) as ScanRequestChanges)
        );
    }

    getLatestReportPages(targetId: TargetId, category: ScanCategory, params?: PaginationParams): Observable<Paginated<Page>>;
    getLatestReportPages(targetId: null | undefined, category: ScanCategory | null | undefined, params?: PaginationParams): never;
    getLatestReportPages(targetId: TargetId | null | undefined, category: null | undefined, params?: PaginationParams): never;
    getLatestReportPages(targetId: TargetId | null | undefined, category: ScanCategory | null | undefined, params?: PaginationParams): Observable<Paginated<Page>> | never;
    getLatestReportPages(targetId: TargetId | null | undefined, category: ScanCategory | null | undefined, params?: PaginationParams) {
        if (!targetId) {
            throw new Error('`targetId` is required');
        } else if (!category) {
            throw new Error('`category` is required');
        }

        const urlSearchParams = createPaginationSearchParams(params, { category });

        return this._http.get<Paginated<Page>>(`${this._baseUrl}/${targetId}/latest-report/pages?${urlSearchParams.toString()}`);
    }

    getLatestCategoryReport(targetId: TargetId, category: ScanCategory): Observable<Report>;
    getLatestCategoryReport(targetId: null | undefined, category: ScanCategory | null | undefined): never;
    getLatestCategoryReport(targetId: TargetId | null | undefined, category: null | undefined): never;
    getLatestCategoryReport(targetId: TargetId | null | undefined, category: ScanCategory | null | undefined): Observable<Report> | never;
    getLatestCategoryReport(targetId: TargetId | null | undefined, category: ScanCategory | null | undefined) {
        return this._http.get<Report>(`${this._baseUrl}/${targetId}/latest-report/${category}`);
    }

    getMetricsHistory(targetId: TargetId, category: ScanCategory): Observable<HistoricalReportMetrics[]>;
    getMetricsHistory(targetId: null | undefined, category: ScanCategory | null | undefined): never;
    getMetricsHistory(targetId: TargetId | null | undefined, category: null | undefined): never;
    getMetricsHistory(targetId: TargetId | null | undefined, category: ScanCategory | null | undefined): Observable<HistoricalReportMetrics[]> | never;
    getMetricsHistory(targetId: TargetId | null | undefined, category: ScanCategory | null | undefined) {
        return this._http.get<HistoricalReportMetrics[]>(`${this._baseUrl}/${targetId}/latest-report/${category}/metrics/history`);
    }
}

